
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PlanCard } from "./PlanCard";
import { CreatePlanCard } from "./CreatePlanCard";
import { useToast } from "@/hooks/use-toast";

interface Plan {
  id: string;
  feature_id: string;
  name: string;
  price: number;
  duration: number;
  type: string;
  status: string;
  products: {
    name: string;
  } | null;
}

interface GroupedPlans {
  [key: string]: Plan[];
}

export const PlansGrid = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from("plans")
        .select(`
          *,
          products:feature_id (
            name
          )
        `);

      if (error) throw error;
      setPlans(data || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch plans",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      // First check if there are any active subscriptions for this plan
      const { data: activeSubscriptions, error: subscriptionError } = await supabase
        .from("subscriptions")
        .select("id")
        .eq("plan_id", id)
        .neq("status", "cancelled");

      if (subscriptionError) throw subscriptionError;

      if (activeSubscriptions && activeSubscriptions.length > 0) {
        toast({
          variant: "destructive",
          title: "Cannot Delete Plan",
          description: "This plan has active subscriptions and cannot be deleted.",
        });
        return;
      }

      const { error } = await supabase
        .from("plans")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Plan deleted successfully",
      });
      
      fetchPlans();
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete plan",
      });
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  // Group plans by product name
  const groupedPlans = plans.reduce((acc: GroupedPlans, plan) => {
    const productName = plan.products?.name || 'Uncategorized';
    if (!acc[productName]) {
      acc[productName] = [];
    }
    acc[productName].push(plan);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {Object.entries(groupedPlans).map(([productName, productPlans]) => (
        <div key={productName} className="space-y-4">
          <h2 className="text-2xl font-bold text-primary">{productName}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {productPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                onDelete={handleDelete}
                onUpdate={fetchPlans}
              />
            ))}
          </div>
        </div>
      ))}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <CreatePlanCard onPlanCreated={fetchPlans} />
      </div>
    </div>
  );
};
